import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { api, kanalSeiteApi, posteingangApi } from "../api";
import { applyTheme, getStoredTheme, setStoredTheme, type Theme } from "../theme";
import { can, ROLE_LABELS, type Permission, type UserRole } from "@webradiosuite/shared";
import { AlertBanner } from "./playout-ui";
import { LogoZeile } from "./Logo";
// h-ui: Support-Band und Hinweis bei erreichter Tarifgrenze
import { GrenzeHinweis, SupportBand } from "./hosting-ui";

const STORAGE_KEY_MOBILE = "wr.mobile-sidebar-collapsed";

// Ein Strichsymbol je Eintrag. Alle im selben Raster (24x24, Strich 1.8),
// damit die Leiste ruhig bleibt - ungleiche Symbole sind unruhiger als gar
// keine. Der Pfad steht hier und nicht in 25 einzelnen Dateien.
const SYMBOLE: Record<string, string> = {
  "/dashboard": "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  "/posteingang": "M4 13h4l2 3h4l2-3h4M4 13l2-7h12l2 7v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z",
  "/servers": "M4 5h16v5H4zM4 14h16v5H4zM7.5 7.5h.01M7.5 16.5h.01",
  // Das Zeichen der Suite als Symbol: Mast mit Wellen.
  "/channels": "M12 10v4M15 9a4 4 0 0 1 0 6M9 9a4 4 0 0 0 0 6M18 6a8 8 0 0 1 0 12M6 6a8 8 0 0 0 0 12",
  "/clocks": "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
  "/studio": "M5 4v7M5 15v5M12 4v3M12 11v9M19 4v9M19 17v3M3 13h4M10 9h4M17 15h4",
  "/practice": "M12 4a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V7a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v4M9 21h6",
  "/library": "M9 18V6l10-2v12M6.5 18a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0zM16.5 16a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z",
  "/news": "M4 6h11v14H5a1 1 0 0 1-1-1zM15 10h5v9a1 1 0 0 1-1 1h-4M7 9h5M7 12h5M7 15h5",
  "/redaktion": "M4 20h4l10-10-4-4L4 16zM14 6l4 4",
  "/shows": "M4 6h16v14H4zM4 10h16M8 4v3M16 4v3M11 13l3 2-3 2z",
  "/moderators": "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 20a7 7 0 0 1 14 0",
  "/kalender": "M4 6h16v14H4zM4 10h16M8 4v3M16 4v3",
  "/mitmachen": "M6 20V10M12 20V4M18 20v-7",
  "/gewinnspiel": "M4 11h16v9H4zM4 7h16v4H4zM12 7v13M12 7c-1-3-5-3-5 0M12 7c1-3 5-3 5 0",
  "/frage-der-woche": "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.7M12 17h.01",
  "/telefon": "M6 3h3l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4 5.2 2 2 0 0 1 6 3z",
  "/chat": "M4 5h16v10H9l-5 4z",
  "/stats": "M4 19h16M6 15l4-5 3 3 5-6",
  "/reports": "M9 4h6v3H9zM7 5H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1M8 11h8M8 15h5",
  "/mitschnitt": "M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  "/branding": "M12 20a8 8 0 1 1 0-16c4.4 0 8 3.1 8 7 0 2.2-1.8 3-3.5 3H15a2 2 0 0 0-1.4 3.4c.3.4.4.8.4 1.1 0 .8-.9 1.5-2 1.5zM8 8h.01M7 12h.01M10.5 6h.01",
  "/seiten": "M4 5h16v14H4zM4 9h16M9 9v10",
  "/module": "M4 8h10M18 8h2M4 16h4M12 16h8M14 6v4M8 14v4",
  "/team": "M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 20a6 6 0 0 1 12 0M16 4.8a4 4 0 0 1 0 7.4M17 14c2.5.6 4 2.5 4 6",
  "/veranstaltungen": "M4 9V7h16v2a2 2 0 0 0 0 6v2H4v-2a2 2 0 0 0 0-6zM10 7v10",
  // Werbeplanung (f-werbung): Megafon.
  "/werbung": "M4 10v4h3l8 4V6l-8 4zM7 14l1.5 5h2.5l-1-4.5M18.5 9.5a3.5 3.5 0 0 1 0 5",
  // h-ui: Hosting – Tarif (Tacho), Radios (Stapel), Tarife (Etikett), Nutzung (Balken)
  "/tarif": "M4 16a8 8 0 1 1 16 0M12 16l4-5M4 20h16",
  // h-domain: Globus für „Eigene Adresse“
  "/adresse": "M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3",
  // h-umzug: Import (Pfeil in eine Kiste), Löschnachweise (Schild mit Haken), Meine Daten (Ordner mit Pfeil)
  "/betrieb/import": "M12 3v10M8 9l4 4 4-4M4 14v5h16v-5",
  "/betrieb/loeschnachweise": "M12 3l7 3v6c0 4.2-3 7.4-7 9-4-1.6-7-4.8-7-9V6zM9 12l2 2 4-4",
  "/meine-daten": "M3 7V5h7l2 2h9v12H3zM12 10v6M9 13l3 3 3-3",
  "/betrieb/radios": "M4 5h16v4H4zM4 11h16v4H4zM4 17h16v2H4zM7 7h.01M7 13h.01",
  "/betrieb/tarife": "M4 12V4h8l8 8-8 8zM8 8h.01",
  "/betrieb/nutzung": "M4 20V10M10 20V4M16 20v-7M22 20H2",
};

function Symbol({ to, className = "opacity-80" }: { to: string; className?: string }): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
         className={`flex-shrink-0 ${className}`} aria-hidden="true" focusable="false">
      <path d={SYMBOLE[to] ?? "M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"} />
    </svg>
  );
}

// Nach Aufgaben gruppiert, damit auch Neue schnell finden, was sie suchen.
//
// Die Ordnung folgt dem Tag: Was anliegt (oben), der Sendebetrieb, was ins
// Programm kommt, was von Hörern kommt, was man hinterher auswertet, wie es
// aussieht, und zuletzt die Verwaltung. Innerhalb einer Gruppe steht das
// Häufigste vorn.
const navGroups: Array<{
  label: string | null;
  items: Array<{ to: string; label: string; modules?: string[]; recht?: Permission; nurBetreiber?: boolean }>;
}> = [
  { label: null, items: [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/posteingang", label: "Posteingang", recht: "interaction.moderate" },
  ] },
  { label: "Sendebetrieb", items: [
    { to: "/studio", label: "Studio" },
    { to: "/servers", label: "Server & Sendung", recht: "channel.manage" },
    { to: "/channels", label: "Kanäle", recht: "channel.manage" },
    { to: "/clocks", label: "Stundenuhren", recht: "schedule.write" },
    { to: "/practice", label: "Probebetrieb", recht: "practice.use" },
  ] },
  { label: "Programm", items: [
    { to: "/shows", label: "Sendungen", recht: "shows.write" },
    { to: "/moderators", label: "Moderatoren", recht: "shows.write" },
    { to: "/library", label: "Mediathek", recht: "library.write" },
    { to: "/news", label: "Nachrichten", recht: "news.write" },
    { to: "/werbung", label: "Werbeplanung", recht: "schedule.write" },
    { to: "/redaktion", label: "Redaktion", recht: "posts.write" },
  ] },
  // Alles, was von Hörern kommt - früher stand das zwischen den Sendersachen
  // und war dort nicht zu finden.
  { label: "Hörer", items: [
    { to: "/mitmachen", label: "Mitmachen", modules: ["poll", "quiz"], recht: "interaction.moderate" },
    { to: "/gewinnspiel", label: "Gewinnspiel", modules: ["contest"], recht: "interaction.moderate" },
    { to: "/frage-der-woche", label: "Frage der Woche", modules: ["voice_survey"], recht: "interaction.moderate" },
    { to: "/telefon", label: "Hörertelefon", modules: ["phone"], recht: "interaction.moderate" },
    { to: "/chat", label: "Hörerchat", modules: ["chat"], recht: "interaction.moderate" },
    { to: "/kalender", label: "Mitmach-Kalender", modules: ["events"], recht: "events.write" },
  ] },
  { label: "Auswerten", items: [
    { to: "/stats", label: "Statistik", recht: "stats.read" },
    { to: "/reports", label: "Meldelisten", recht: "reports.read" },
    { to: "/mitschnitt", label: "Mitschnitt", recht: "recordings.read" },
  ] },
  { label: "Aussehen", items: [
    { to: "/branding", label: "Hörerseite & Farben", recht: "site.write" },
    { to: "/seiten", label: "Seiten bauen", recht: "site.write" },
    // h-domain: eigene Domain für die Hörerseite des Radios
    { to: "/adresse", label: "Eigene Adresse", recht: "tenant.manage" },
    { to: "/module", label: "Mitmach-Module", recht: "tenant.manage" },
  ] },
  { label: "Verwalten", items: [
    { to: "/team", label: "Team & Rollen", recht: "users.manage" },
    { to: "/veranstaltungen", label: "Private Veranstaltungen", modules: ["veranstaltung"], recht: "events.write" },
    // h-ui: Tarif und Nutzung des eigenen Radios
    { to: "/tarif", label: "Mein Tarif", recht: "tenant.manage" },
    // h-umzug: Datenauskunft und Datenmitnahme des eigenen Radios
    { to: "/meine-daten", label: "Meine Daten", recht: "tenant.manage" },
  ] },
  // h-ui: Betreiber-Konsole – nur für die Rolle „Betreiber“ (super_admin).
  // Während eines Support-Zugriffs ist man tenant_admin und sieht sie nicht.
  { label: "Betrieb", items: [
    { to: "/betrieb/radios", label: "Radios", nurBetreiber: true },
    { to: "/betrieb/tarife", label: "Tarife", nurBetreiber: true },
    { to: "/betrieb/nutzung", label: "Nutzung", nurBetreiber: true },
    // h-umzug
    { to: "/betrieb/import", label: "Import", nurBetreiber: true },
    { to: "/betrieb/loeschnachweise", label: "Löschnachweise", nurBetreiber: true },
  ] },
];

// Anwärter sehen nur, was sie auch dürfen: üben und das Studio verbinden.
const TRAINEE_NAV = ["/practice", "/studio"];

// Was zu einem ausgeschalteten Mitmach-Modul gehört, steht gar nicht erst im Menü.
// f-web: `zusatz` sind Menüpunkte, die nicht aus der Radiorolle folgen, sondern
// aus einer Kanalrolle – z. B. „Seiten bauen“ für ein Kanalteam mit eigener Hörerseite.
function navFor(role: string | undefined, modules: string[], zusatz: string[] = []): typeof navGroups {
  // Drei Bedingungen: Modul eingeschaltet, Recht vorhanden, und Anwaerter sehen
  // ohnehin nur ihre zwei Seiten. Das Ausblenden ist Bequemlichkeit - die
  // Entscheidung faellt serverseitig bei jeder Anfrage.
  const erlaubt = (item: { to: string; modules?: string[]; recht?: Permission; nurBetreiber?: boolean }): boolean =>
    (!item.nurBetreiber || role === "super_admin") && // h-ui
    (!item.modules || item.modules.some((m) => modules.includes(m))) &&
    (!item.recht || (role ? can(role, item.recht) : false) || zusatz.includes(item.to)) &&
    (role !== "trainee" || TRAINEE_NAV.includes(item.to));
  return navGroups
    .map((group) => ({ ...group, items: group.items.filter(erlaubt) }))
    .filter((group) => group.items.length > 0);
}

// Seiten außerhalb des Menüs, die trotzdem einen Namen in der Kopfleiste
// brauchen.
type Eintrag = { to: string; label: string; gruppe: string | null };

const EXTRA_SEITEN: Eintrag[] = [
  { to: "/account", label: "Mein Konto", gruppe: null },
  { to: "/welcome", label: "Willkommen", gruppe: null },
];

/** Wo man gerade ist: Gruppe und Seite, aus dem Menü abgeleitet. */
function wo(pathname: string): { gruppe: string | null; seite: string } | null {
  // Der längste Treffer gewinnt: /channels/live/playout gehört zu „Kanäle".
  const alle: Eintrag[] = [
    ...navGroups.flatMap((g) => g.items.map((it) => ({ to: it.to, label: it.label, gruppe: g.label }))),
    ...EXTRA_SEITEN,
  ];
  const best = alle
    .filter((e) => pathname === e.to || pathname.startsWith(`${e.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return best ? { gruppe: best.gruppe, seite: best.label } : null;
}

// Kleine Strichsymbole für Kopfleiste und Menü – im selben Raster wie oben.
function Icon({ d, size = 16, className = "" }: { d: string; size?: number; className?: string }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 ${className}`} aria-hidden="true" focusable="false">
      <path d={d} />
    </svg>
  );
}
const ICON = {
  sonne: "M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  mond: "M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z",
  system: "M4 5h16v11H4zM9 20h6M12 16v4",
  suche: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4",
  burger: "M4 7h16M4 12h16M4 17h16",
  kreuz: "M6 6l12 12M18 6L6 18",
  konto: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 20a7 7 0 0 1 14 0",
  abmelden: "M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 16l4-4-4-4M14 12H4",
  auf_ab: "M8 9l4-4 4 4M8 15l4 4 4-4",
  pfeil: "M5 12h14M13 6l6 6-6 6",
  enter: "M20 5v7a3 3 0 0 1-3 3H6M10 11l-4 4 4 4",
};

const themeOptions: Array<{ value: Theme; label: string; icon: string }> = [
  { value: "light", label: "Hell", icon: ICON.sonne },
  { value: "dark", label: "Dunkel", icon: ICON.mond },
  { value: "system", label: "System", icon: ICON.system },
];

/** Hell/Dunkel/System als Segmentschalter – für das Nutzermenü. */
function ThemeToggle({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }): JSX.Element {
  return (
    <div className="flex gap-1 rounded-lg bg-white/[0.06] p-1 ring-1 ring-inset ring-white/10" role="radiogroup" aria-label="Darstellung">
      {themeOptions.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={theme === opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1 py-1.5 text-[11px] font-medium ${
            theme === opt.value ? "bg-white/[0.14] text-white shadow-sm" : "text-white/55 hover:text-white"
          }`}
          title={`Darstellung: ${opt.label}`}
        >
          <Icon d={opt.icon} size={14} />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Zwei Buchstaben für den Avatar: aus dem Namen, sonst aus der E-Mail. */
function initialen(text: string): string {
  const teile = text.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const a = teile[0]?.[0] ?? "?";
  const b = teile.length > 1 ? teile[teile.length - 1]![0] : teile[0]?.[1] ?? "";
  return (a + b).toUpperCase();
}

function rollenName(role: string | undefined): string {
  if (!role) return "";
  return ROLE_LABELS[role as UserRole] ?? role;
}

/**
 * Kleine Anzeige in der Kopfleiste: sendet etwas, ist jemand live?
 *
 * Fragt dieselbe Übersicht wie das Dashboard, aber seltener. Wer sie nicht
 * lesen darf oder wenn die API nicht antwortet, sieht einfach nichts – die
 * Anzeige ist Beiwerk, keine Störungsmeldung (die steht im AlertBanner).
 */
function LiveStatus(): JSX.Element | null {
  const [lage, setLage] = useState<{ live: boolean; senden: number; aktiv: number } | null>(null);
  useEffect(() => {
    let aktiv = true;
    const hole = (): void => {
      api.getEngineOverview()
        .then((o) => {
          if (!aktiv) return;
          setLage({
            live: o.channels.some((c) => c.now_playing?.on_air === "live"),
            senden: o.channels.filter((c) => c.engine?.state === "running").length,
            aktiv: o.channels.filter((c) => c.status === "active").length,
          });
        })
        .catch(() => { if (aktiv) setLage(null); });
    };
    hole();
    const t = setInterval(hole, 30_000);
    return () => { aktiv = false; clearInterval(t); };
  }, []);
  if (!lage || lage.aktiv === 0) return null;
  const text = lage.live ? "Live" : lage.senden > 0 ? "Auf Sendung" : "Sendepause";
  return (
    <span
      className={`hidden items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium sm:inline-flex ${
        lage.live
          ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
          : lage.senden > 0
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            : "border-default bg-card text-muted"
      }`}
      title={`${lage.senden} von ${lage.aktiv} aktiven Kanälen senden`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${lage.live ? "animate-live-pulse bg-red-500" : lage.senden > 0 ? "bg-emerald-500" : "bg-gray-400"}`} aria-hidden="true" />
      {text}
      <span className="tabular-nums opacity-70">{lage.senden}/{lage.aktiv}</span>
    </span>
  );
}

/**
 * Schnellsuche (Strg/⌘+K): alle Seiten, die diese Rolle sehen darf, per
 * Tastatur erreichbar. Nur Navigation – sie sucht nicht in Inhalten.
 */
function Befehlspalette({ eintraege, onClose }: { eintraege: Eintrag[]; onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const treffer = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return eintraege;
    return eintraege.filter((e) => `${e.label} ${e.gruppe ?? ""} ${e.to}`.toLowerCase().includes(s));
  }, [q, eintraege]);

  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const geh = (e: Eintrag | undefined): void => {
    if (!e) return;
    onClose();
    navigate(e.to);
  };

  const onKey = (e: ReactKeyboardEvent): void => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(treffer.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); geh(treffer[idx]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Schnellsuche">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-default bg-card shadow-lg animate-fade-in">
        <div className="flex items-center gap-3 border-b border-default px-4">
          <Icon d={ICON.suche} size={18} className="text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Wohin? Seite suchen …"
            className="h-12 flex-1 border-0 bg-transparent px-0 text-[0.95rem] text-primary shadow-none outline-none focus:shadow-none focus-visible:shadow-none"
            style={{ boxShadow: "none", background: "transparent" }}
            aria-label="Seite suchen"
            aria-controls="palette-liste"
            aria-activedescendant={treffer[idx] ? `palette-${idx}` : undefined}
            role="combobox"
            aria-expanded="true"
          />
          <kbd className="hidden rounded border border-default bg-input px-1.5 py-0.5 text-[0.7rem] text-muted sm:inline">Esc</kbd>
        </div>
        <ul id="palette-liste" ref={listRef} role="listbox" className="max-h-[50vh] overflow-y-auto p-2">
          {treffer.length === 0 && (
            <li className="px-3 py-8 text-center text-sm text-muted">Keine Seite gefunden.</li>
          )}
          {treffer.map((e, i) => (
            <li
              key={e.to}
              id={`palette-${i}`}
              data-idx={i}
              role="option"
              aria-selected={i === idx}
              onMouseMove={() => setIdx(i)}
              onClick={() => geh(e)}
              className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${
                i === idx ? "bg-brand-primary/10 text-primary" : "text-secondary"
              }`}
            >
              <span className={`flex h-8 w-8 items-center justify-center rounded-md ${i === idx ? "bg-brand-primary/15 text-brand-primary" : "bg-input text-muted"}`}>
                <Symbol to={e.to} className="" />
              </span>
              <span className="min-w-0 flex-1 truncate font-medium">{e.label}</span>
              {e.gruppe && <span className="text-xs text-muted">{e.gruppe}</span>}
              {i === idx && <Icon d={ICON.enter} size={14} className="text-muted" />}
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-4 border-t border-default px-4 py-2 text-[0.7rem] text-muted">
          <span className="inline-flex items-center gap-1"><Icon d={ICON.auf_ab} size={12} /> auswählen</span>
          <span className="inline-flex items-center gap-1"><Icon d={ICON.enter} size={12} /> öffnen</span>
          <span className="ml-auto">{treffer.length} {treffer.length === 1 ? "Seite" : "Seiten"}</span>
        </div>
      </div>
    </div>
  );
}

/** Die Karte unten in der Leiste: wer angemeldet ist, dazu das Menü. */
function Nutzerkarte({ theme, onTheme, onLogout, onNavigate }: {
  theme: Theme;
  onTheme: (t: Theme) => void;
  onLogout: () => void;
  onNavigate: () => void;
}): JSX.Element | null {
  const { user } = useAuth();
  const [offen, setOffen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Klick daneben oder Escape schließt das Menü.
  useEffect(() => {
    if (!offen) return;
    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOffen(false);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOffen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [offen]);

  if (!user) return null;
  const name = user.display_name || user.email.split("@")[0] || user.email;
  return (
    <div ref={boxRef} className="relative border-t border-white/[0.08] p-3">
      {offen && (
        <div className="absolute inset-x-3 bottom-full mb-2 space-y-1 rounded-xl border border-white/10 bg-[#171a21] p-2 shadow-lg animate-fade-in" role="menu">
          <NavLink
            to="/account"
            role="menuitem"
            onClick={() => { setOffen(false); onNavigate(); }}
            className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-white/80 hover:bg-white/[0.06] hover:text-white"
          >
            <Icon d={ICON.konto} /> Mein Konto
          </NavLink>
          <div className="px-1 pb-1 pt-1">
            <div className="mb-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">Darstellung</div>
            <ThemeToggle theme={theme} onChange={onTheme} />
          </div>
          <div className="my-1 h-px bg-white/[0.08]" />
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-white/80 hover:bg-white/[0.06] hover:text-white"
          >
            <Icon d={ICON.abmelden} /> Abmelden
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={offen}
        className={`flex w-full items-center gap-3 rounded-xl p-2 text-left ${offen ? "bg-white/[0.08]" : "hover:bg-white/[0.05]"}`}
      >
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-primary/60 text-[0.8rem] font-semibold text-white ring-2 ring-white/10">
          {initialen(name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-white">{name}</span>
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5">
            <span className="flex-shrink-0 rounded-full bg-brand-primary/15 px-1.5 py-px text-[10px] font-medium text-brand-primary ring-1 ring-inset ring-brand-primary/25" title={`Rolle: ${user.role}`}>
              {rollenName(user.role)}
            </span>
            <span className="truncate text-xs text-white/50">{user.email}</span>
          </span>
        </span>
        <Icon d={ICON.auf_ab} size={14} className="text-white/40" />
      </button>
    </div>
  );
}

function IconKnopf({ label, onClick, children, className = "" }: { label: string; onClick: () => void; children: ReactNode; className?: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-surface-hover hover:text-primary ${className}`}
    >
      {children}
    </button>
  );
}

export function Layout(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [modules, setModules] = useState<string[]>([]);
  const [offenePost, setOffenePost] = useState<number>(0);
  const [theme, setTheme] = useState<Theme>(getStoredTheme());
  const [paletteOffen, setPaletteOffen] = useState(false);
  // Welches Theme gerade wirklich gilt – bei „System" entscheidet das Gerät.
  const [dunkel, setDunkel] = useState<boolean>(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    applyTheme(getStoredTheme());
  }, []);

  const waehleTheme = useCallback((t: Theme): void => {
    setStoredTheme(t);
    setTheme(t);
    setDunkel(document.documentElement.classList.contains("dark"));
  }, []);

  // Folgt das Theme dem System, kann es sich unter uns ändern.
  useEffect(() => {
    const obs = new MutationObserver(() => setDunkel(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Welche Mitmach-Module an sind, entscheidet mit, was im Menü steht.
  useEffect(() => {
    api.listModules()
      .then((list) => setModules(list.filter((m) => m.enabled).map((m) => m.id)))
      .catch(() => setModules([]));
  }, []);

  // f-web: Wer „Seiten bauen“ nicht schon über die Radiorolle sieht, bekommt
  // den Punkt, sobald er in einem Kanal mit eingeschalteter Hörerseite
  // pflegen darf (Kanalrolle mit site.write). Bequemlichkeit – der Server prüft.
  const [zusatzNav, setZusatzNav] = useState<string[]>([]);
  useEffect(() => {
    if (!user?.role || user.role === "trainee" || can(user.role, "site.write")) { setZusatzNav([]); return; }
    kanalSeiteApi.bereiche()
      .then((b) => setZusatzNav(b.some((x) => x.aktiv && x.rechte.pflegen) ? ["/seiten"] : []))
      .catch(() => setZusatzNav([]));
  }, [user?.role]);

  // Wie viel im Posteingang wartet. Zwei Minuten reichen: Die Zahl soll
  // auffallen, nicht flackern. Wer nichts moderieren darf, fragt gar nicht.
  useEffect(() => {
    if (!user?.role || !can(user.role, "interaction.moderate")) return;
    const hole = (): void => {
      posteingangApi.zaehler().then((z) => setOffenePost(z.offen)).catch(() => undefined);
    };
    hole();
    const t = setInterval(hole, 120_000);
    return () => clearInterval(t);
  }, [user?.role]);

  // Auto-close mobile drawer on resize to desktop
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e: MediaQueryListEvent): void => { if (e.matches) setMobileOpen(false); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Close drawer on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Strg+K bzw. ⌘K öffnet die Schnellsuche – von überall.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMobileOpen(false);
        setPaletteOffen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onLogout = async (): Promise<void> => {
    await logout();
    navigate("/login", { replace: true });
  };

  const gruppen = navFor(user?.role, modules, zusatzNav);
  // Die Schnellsuche kennt genau das, was auch im Menü steht – plus das Konto.
  const eintraege = useMemo<Eintrag[]>(
    () => [
      ...gruppen.flatMap((g) => g.items.map((it) => ({ to: it.to, label: it.label, gruppe: g.label }))),
      { to: "/account", label: "Mein Konto", gruppe: null },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.role, modules.join(","), zusatzNav.join(",")]
  );
  const hier = wo(pathname);
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    // h-ui: Außen eine Spalte, damit das Support-Band über allem steht.
    <div className="flex h-full flex-col">
    <SupportBand />
    <div className="flex min-h-0 flex-1 bg-app">
      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-[2px] md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Die Leiste ist in beiden Modi tiefdunkel: Sie ist der ruhende Rahmen,
          der Inhalt daneben wechselt Hell und Dunkel. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] transform bg-[var(--bg-sidebar)] text-white transition-transform duration-200 ease-out md:relative md:w-64 md:translate-x-0 ${
          mobileOpen ? "translate-x-0 shadow-lg" : "-translate-x-full md:translate-x-0"
        } flex flex-shrink-0 flex-col border-r border-white/[0.06]`}
        aria-label="Hauptmenü"
      >
        <div className="px-4 pb-3 pt-4">
          <div className="flex items-center justify-between">
            <LogoZeile />
            {/* Mobile-Close-Button */}
            <button
              onClick={() => setMobileOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white md:hidden"
              aria-label="Menü schließen"
            >
              <Icon d={ICON.kreuz} size={20} />
            </button>
          </div>
          {/* Die Senderkarte: für welches Radio man gerade arbeitet. */}
          {user?.tenant_name && (
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/[0.04] p-2.5 ring-1 ring-inset ring-white/[0.08]">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-primary/20 text-xs font-bold text-brand-primary ring-1 ring-inset ring-brand-primary/30">
                {initialen(user.tenant_name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-white">{user.tenant_name}</span>
                <span className="block text-[11px] text-white/45">Sender</span>
              </span>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {gruppen.map((group) => (
            <div key={group.label ?? "start"} className="pb-2">
              {group.label && (
                <div className="px-3 pb-1.5 pt-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition md:py-[7px] ${
                        isActive
                          ? "bg-white/[0.08] font-medium text-white"
                          : "text-white/65 hover:bg-white/[0.04] hover:text-white"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Der Balken links zeigt, wo man ist - auch für Leute, die
                            Farbunterschiede schlecht sehen. */}
                        {isActive && <span className="absolute -left-3 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-primary" aria-hidden="true" />}
                        <Symbol to={item.to} className={isActive ? "text-brand-primary" : "opacity-70 group-hover:opacity-100"} />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.to === "/posteingang" && offenePost > 0 && (
                          <span className="rounded-full bg-brand-primary px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white tabular-nums">
                            {offenePost > 99 ? "99+" : offenePost}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <Nutzerkarte
          theme={theme}
          onTheme={waehleTheme}
          onLogout={() => void onLogout()}
          onNavigate={() => setMobileOpen(false)}
        />
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        {/* Kopfleiste: wo man ist, Schnellsuche, Sendestatus, Hell/Dunkel.
            Halbdurchsichtig, damit beim Scrollen der Inhalt durchschimmert. */}
        <header
          className="sticky top-0 z-20 flex h-14 flex-shrink-0 items-center gap-2 border-b border-default px-3 backdrop-blur-md sm:px-6 lg:px-8"
          style={{ background: "color-mix(in srgb, var(--bg-app) 78%, transparent)" }}
        >
          <IconKnopf label="Menü öffnen" onClick={() => setMobileOpen(true)} className="-ml-1 md:hidden">
            <Icon d={ICON.burger} size={20} />
          </IconKnopf>
          <nav aria-label="Brotkrumen" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
            {hier?.gruppe && (
              <>
                <span className="hidden truncate text-muted sm:inline">{hier.gruppe}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hidden flex-shrink-0 text-muted opacity-60 sm:block" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
              </>
            )}
            <span className="truncate font-medium text-primary">{hier?.seite ?? ""}</span>
          </nav>
          <button
            type="button"
            onClick={() => setPaletteOffen(true)}
            className="flex h-9 items-center gap-2 rounded-lg border border-default bg-card px-2.5 text-sm text-muted shadow-sm hover:text-primary sm:w-56 sm:px-3"
            aria-label="Schnellsuche öffnen"
            aria-keyshortcuts="Control+K Meta+K"
          >
            <Icon d={ICON.suche} />
            <span className="hidden flex-1 text-left sm:inline">Suchen …</span>
            <kbd className="hidden rounded border border-default bg-input px-1.5 font-sans text-[0.7rem] text-muted sm:inline">{mac ? "⌘K" : "Strg K"}</kbd>
          </button>
          <LiveStatus />
          <IconKnopf label={dunkel ? "Helle Darstellung" : "Dunkle Darstellung"} onClick={() => waehleTheme(dunkel ? "light" : "dark")}>
            <Icon d={dunkel ? ICON.sonne : ICON.mond} size={18} />
          </IconKnopf>
        </header>

        {/* Inhalt in fester Breite und mittig: Auf breiten Bildschirmen laufen
            Zeilen sonst über die ganze Wand, und das Auge verliert beim
            Zeilenwechsel den Anschluss. 1400 Pixel sind breit genug für Tabellen
            mit vielen Spalten und schmal genug zum Lesen. */}
        <div className="mx-auto w-full max-w-[1400px] flex-1">
          <AlertBanner />
          <GrenzeHinweis />
          <Outlet />
        </div>
      </main>

      {paletteOffen && <Befehlspalette eintraege={eintraege} onClose={() => setPaletteOffen(false)} />}
    </div>
    </div>
  );
}
